import * as React from 'react';
import {Prompt} from 'react-router-dom';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {Link} from 'shells/link/link';

type Props = {
    /**
     * Whether routePaths is a list of allowed (unprompted) or blocked (prompted) routes.
     */
    routeListType: 'allow' | 'block';
    /**
     * List of route paths.
     * e.g. ['/foo', '/bar']
     */
    routePaths: string[];
    /**
     * Whether the pathname must match exactly or just start with the route path.
     * e.g. given the routePath '/foo/bar',
     *      when isExact:
     *          matches pathname '/foo/bar' but not '/foo/bar/baz'
     *      when not isExact:
     *          matches pathname '/foo/bar' and '/foo/bar/baz'
     */
    isExact?: boolean;
};

export function PromptedRoute(props: Props) {
    const {routeListType, routePaths, isExact} = props;

    const [promptedPathname, setPromptedPathname] = React.useState<string | null | undefined>(
        undefined
    );

    return (
        <>
            <Prompt
                message={(location) => {
                    if (isPromptedRoute(location.pathname, routePaths, routeListType, isExact)) {
                        // State to show our custom confirmation prompt
                        setPromptedPathname(location.pathname);

                        // Returning false will block the route, but not show the default prompt
                        return false;
                    }

                    // Returning true will allow the route to be accessed without showing any prompt
                    return true;
                }}
            />
            {promptedPathname ? (
                <ConfirmationDialog
                    title='Are you sure you’d like to leave?'
                    message='This page will open in a new window.'
                    confirmText={
                        <Link
                            variant='primary'
                            href={`${window.location.origin.replace(
                                'embed',
                                'app'
                            )}${promptedPathname}`}
                            // @ts-expect-error
                            target='_blank'
                            onClick={() => setPromptedPathname(undefined)}
                        >
                            Open page
                        </Link>
                    }
                    isConfirmDestructive={false}
                    onCancel={() => {
                        setPromptedPathname(undefined);
                    }}
                    autoFocusCancelButton={false}
                />
            ) : undefined}
        </>
    );
}

/**
 * Exported for testability: returns true a prompt should be displayed for the given pathname
 *
 * @param pathname
 * @param routePaths
 * @param routeListType
 * @param isExact
 * @returns {boolean|boolean}
 */
export const isPromptedRoute = (
    pathname: string,
    routePaths: string[],
    routeListType: 'allow' | 'block',
    isExact?: boolean
): boolean => {
    const routeInList = isExact
        ? routePaths.includes(pathname)
        : Boolean(routePaths.find((routePath) => pathname.startsWith(routePath)));

    return routeListType === 'allow'
        ? // not prompted if it is in allow list
          !routeInList
        : // prompted if it is in block list
          routeInList;
};
